import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CLIENTS, PROFIT_OPTIONS } from '@/mocks/users';

interface ToastData {
  name: string;
  profitLabel: string;
  amount: number;
  avatar: string;
}

const AVATAR_COLORS = [
  'linear-gradient(135deg, #16a34a, #22c55e)',
  'linear-gradient(135deg, #15803d, #4ade80)',
  'linear-gradient(135deg, #166534, #86efac)',
  'linear-gradient(135deg, #14532d, #22c55e)',
  'linear-gradient(135deg, #052e16, #16a34a)',
];

const getRandomToast = (): ToastData => {
  const name = CLIENTS[Math.floor(Math.random() * CLIENTS.length)];
  const profit = PROFIT_OPTIONS[Math.floor(Math.random() * PROFIT_OPTIONS.length)];
  return {
    name,
    profitLabel: profit.label,
    amount: profit.amount,
    avatar: AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)],
  };
};

const EarningToast = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState<ToastData | null>(null);
  const hideTimerRef = useRef<number>(0);

  const showToast = () => {
    setData(getRandomToast());
    setVisible(true);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => setVisible(false), 3500);
  };

  useEffect(() => {
    const initial = window.setTimeout(showToast, 1500);
    const interval = window.setInterval(showToast, 5000);
    return () => {
      clearTimeout(initial);
      clearInterval(interval);
      clearTimeout(hideTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return null;

  return (
    <div
      className="fixed bottom-6 left-0 z-50 pointer-events-none"
      aria-hidden="true"
      style={{
        transform: visible ? 'translateX(0)' : 'translateX(-110%)',
        transition: 'transform 0.45s cubic-bezier(0.34, 1.45, 0.64, 1)',
      }}
    >
      <div
        style={{
          background: 'rgba(3, 10, 3, 0.95)',
          border: '1px solid rgba(34,197,94,0.55)',
          boxShadow: visible
            ? '4px 0 32px rgba(34,197,94,0.2), 0 8px 24px rgba(0,0,0,0.6)'
            : 'none',
          borderRadius: '0 14px 14px 0',
          padding: '10px 16px 10px 12px',
          backdropFilter: 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          minWidth: 220,
          maxWidth: 280,
        }}
      >
        {/* Green glow left bar */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: '20%',
            bottom: '20%',
            width: 3,
            borderRadius: '0 3px 3px 0',
            background: 'linear-gradient(180deg, #22c55e, #16a34a)',
            boxShadow: '0 0 8px rgba(34,197,94,0.8)',
          }}
        />

        {/* Pulse dot */}
        <div style={{ position: 'relative', flexShrink: 0, width: 10, height: 10, marginLeft: 4 }}>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: 'rgba(34,197,94,0.35)',
              animation: visible ? 'earn-ping 1.3s ease-in-out infinite' : 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: 2,
              borderRadius: '50%',
              background: '#22c55e',
              boxShadow: '0 0 6px rgba(34,197,94,1)',
            }}
          />
        </div>

        {/* Avatar */}
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            flexShrink: 0,
            background: data.avatar,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 800,
            color: 'white',
            border: '1.5px solid rgba(34,197,94,0.4)',
          }}
        >
          {data.name.slice(0, 1)}
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0, lineHeight: 1.4 }}>
          <div style={{ color: 'rgba(255,255,255,0.95)', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {data.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10 }}>
              {t('toast.earned')}
            </span>
            <span style={{ color: '#4ade80', fontWeight: 800, fontSize: 13 }}>
              {data.profitLabel}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10 }}>
              (€{data.amount.toLocaleString()})
            </span>
          </div>
        </div>

        {/* Corn */}
        <span style={{ fontSize: 16, flexShrink: 0 }}>🌽</span>
      </div>

      <style>{`
        @keyframes earn-ping {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(2.4); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default EarningToast;
